import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import cx from 'classnames';
import moment from 'moment';
import { useRealtimeTags } from 'Core/hooks';

// Controls
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Popover,
    Box
} from '@mui/material'

// Components
import { NotificationManager } from 'Components/Notifications';
import {
    IconButton,
    TableView,
    TableButtons,
    Button,
    Select
} from "@gpstrackit/gtc-react-controls";
import GadgetDateInfo from '../gadgetDateInfo';

// Actions
import { getGadgetData } from '../../dashboard/redux/slice';
import { sendGenericExport, clearRedux, setDriverPerformanceMainTab } from 'Redux/actions'
// Hooks
import { useWindowDimensions } from '../../../../../core/hooks/old';
import { removeDiacritics } from '../../../.globals/helpers';

// Styles
import './index.scss';

export const TableGadget = ({ gadgetkey, gadgetId, name, filters, config = {}, configHandler, customFilters, renderColumnByType, isStatic, containerDimensions, savePreferences, onHierarchyChange }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { messages } = useIntl();

    // Config
    const {
        columns = [],
        paginationType = 'normal', // 'normal' | 'infinite'
        pageSize = 10,
        enablePagination = true,
        defaultSort
    } = config

    // Local states
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState(pageSize);
    const [sort, setSort] = useState(defaultSort);

    // Redux states
    const user = useSelector(state => state.securityRedux.user);
    const { gadgetsData } = useSelector(state => state.dashboardRedux);
    const allTags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });

    // Table ref
    const tableRef = useRef();

    // Hierarchy config popover state (RiskEventsByTag only)
    const [hierarchyAnchorEl, setHierarchyAnchorEl] = useState(null);
    const [tempHierarchy, setTempHierarchy] = useState({
        tagHierarchyRootId: filters?.tagHierarchyRootId || null,
        tagLevel: filters?.tagLevel || null
    });
    const hierarchyPopoverOpen = Boolean(hierarchyAnchorEl);

    // Sync temp hierarchy when filters change externally
    useEffect(() => {
        setTempHierarchy({
            tagHierarchyRootId: filters?.tagHierarchyRootId || null,
            tagLevel: filters?.tagLevel || null
        });
    }, [filters?.tagHierarchyRootId, filters?.tagLevel]);

    // Hierarchy options: root tags (parentId null, level 0)
    const hierarchyOptions = useMemo(() => {
        if (gadgetkey !== 'RiskEventsByTag' || !allTags?.length) return [];
        return allTags
            .filter(tag => !tag.parentId && tag.level === 0 && !tag.disabled)
            .map(tag => ({ id: String(tag.id), label: tag.label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [allTags, gadgetkey]);

    // Level options based on selected hierarchy
    const hierarchyLevelOptions = useMemo(() => {
        const rootId = tempHierarchy?.tagHierarchyRootId;
        if (!rootId || !allTags?.length) return [];

        const numericRootId = String(rootId);
        const hierarchyTags = allTags.filter(tag => {
            if (String(tag.id) === numericRootId) return true;
            if (!tag.tagPath) return false;
            const pathSegments = String(tag.tagPath).split('/');
            return pathSegments.includes(numericRootId);
        });

        const tagsByLevel = {};
        hierarchyTags.forEach(tag => {
            if (tag.level !== undefined && tag.level !== null) {
                if (!tagsByLevel[tag.level]) tagsByLevel[tag.level] = [];
                tagsByLevel[tag.level].push(tag.label);
            }
        });

        return Object.keys(tagsByLevel)
            .map(Number)
            .sort((a, b) => a - b)
            .map(level => {
                const tagNames = tagsByLevel[level] || [];
                const preview = tagNames.slice(0, 3).join(', ');
                const suffix = tagNames.length > 3 ? '...' : '';
                return {
                    id: String(level),
                    label: `${messages['risk_events_by_tag_level'] || 'Level'} ${level} (${preview}${suffix})`
                };
            });
    }, [tempHierarchy?.tagHierarchyRootId, allTags, messages]);

    const gadgetData = gadgetsData[gadgetkey] ? gadgetsData[gadgetkey] : { loading: false, data: { items: [], total: 0, nextToken: null } };
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);
    const { loading, data } = gadgetData;
    const dimensions = useWindowDimensions();

    const buildUtcRange = useCallback((range, frame) => {
        if (!range?.start || !range?.end) {
            return {
                start: range?.start || null,
                end: range?.end || null
            };
        }

        if (frame === 'custom') {
            const startDate = moment(range.start).format('YYYY-MM-DD');
            const endDate = moment(range.end).format('YYYY-MM-DD');

            return {
                start: moment.utc(startDate, 'YYYY-MM-DD').startOf('day').toDate(),
                end: moment.utc(endDate, 'YYYY-MM-DD').endOf('day').toDate()
            };
        }

        return {
            start: range.start,
            end: range.end
        };
    }, []);

    // Memoize filters to avoid unnecessary re-renders when reference changes but content is the same
    const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]);

    // Load more data for infinite scroll using nextToken
    const loadMoreData = React.useCallback(() => {
        if (!loading && paginationType === 'infinite' && data?.nextToken) {
            dispatch(getGadgetData({
                gadgetId,
                gadgetkey,
                pageSize,
                filters: memoizedFilters,
                paginationType,
                sort: sort,
                nextToken: data.nextToken, // Pass the current nextToken
                moreData: true // Indicates that data should be appended to the current list
            }));
        }
    }, [loading, paginationType, dispatch, gadgetId, gadgetkey, pageSize, memoizedFilters, sort, data?.nextToken]);

    // Detect filter changes for both pagination types - reset search
    useEffect(() => {
        if (paginationType === 'normal') {
            dispatch(getGadgetData({
                gadgetId,
                gadgetkey,
                page: 1,
                pageSize,
                filters: memoizedFilters,
                paginationType,
                sort: sort
            }));
        } else if (paginationType === 'infinite') {
            // New search without nextToken
            dispatch(getGadgetData({
                gadgetId,
                gadgetkey,
                pageSize,
                filters: memoizedFilters,
                paginationType,
                sort: sort,
                nextToken: null, // No nextToken = new search
                moreData: false // Create new list with fresh data
            }));
        }
        // NOTE: sort is NOT in dependencies because it's handled in onChangeSort
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memoizedFilters, paginationType, dispatch, gadgetId, gadgetkey, pageSize]);

    useEffect(() => {
        if (exportResult && exportName === `table-gadget-${gadgetkey}`) {
            NotificationManager.info(messages["exportInProcessBody"], messages["exportInProcessTitle"], 4000);
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [exportResult, exportName, dispatch]);

    useEffect(() => {
        if (sort) {
            tableRef?.current?.setSort(sort);
        }
    }, [sort])

    // Get display data
    const displayData = data?.items || [];
    const hasMoreData = paginationType === 'infinite' ? !!data?.nextToken : false;

    // Calculate dynamic height based on container dimensions
    const calculateHeight = () => {
        if (containerDimensions && containerDimensions.height > 0) {
            // Use container dimensions minus header (approx 64px) and padding
            const availableHeight = containerDimensions.height - 64 - 16; // header + padding
            return Math.max(200, availableHeight); // minimum 200px
        }
        // Fallback to previous behavior
        return isStatic ? 425 : dimensions.height - 620;
    };

    const tableHeight = () => {
        if (isStatic && paginationType === 'infinite') {
            return calculateHeight() + 10 // Extra space for infinite scroll
        }
        if (containerDimensions && containerDimensions.height > 0) {
            const contentHeight = calculateHeight();
            return Math.max(150, contentHeight - 50); // Minus content padding
        }
        return isStatic ? 400 : dimensions.height - 620;
    };

    const getColumns = () => {
        const filteredColumns = columns.filter(col => {
            if (col.key === 'riskScore' && gadgetkey === 'RiskEventsByTag') return false;
            if (col.key === 'avgRiskScore' && gadgetkey === 'RiskEventsByDriver') return false;
            return true;
        });
        const headers = filteredColumns.map(col => {
            let title = messages[col.title] || col.title;
            return {
                header: removeDiacritics(title),
                column: col.key,
                functionString: col.functionString || `
                    function splitTextIntoLines(text, maxLength) {
                    let result = "";
                    for (let i = 0; i < text.length; i += maxLength) {
                        result += text.slice(i, i + maxLength) + "\\n";
                    }
                    return result.trim();
                    };

                    const value = row?.${col.key};
                    if (typeof value === "string") {
                        return splitTextIntoLines(value, 40);
                    }

                    if("${col.key}" == "piSuccessRatePercent" && !value){
                        return "0%"
                    }

                    return value ?? "";
                `
            };
        });
        return headers;
    };

    const generateExportStyles = () => {
        return {
            0: {
                cellWidth: 60,
                fontSize: 11,
                fontStyle: "bold",
            }
        };
    };

    return (
        <div className='no-drag' style={{ height: '100%' }}>
            <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardHeader
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Typography variant="h6" component="div">
                                {messages[name] || name}
                            </Typography>
                            <GadgetDateInfo filters={filters} gadgetkey={gadgetkey} />
                        </div>
                    }
                    action={
                        (() => {
                            switch (gadgetkey) {
                                case 'PendingCoachingCase':
                                case 'ActiveCoachingCases':
                                    const buildPendingCaseUrl = () => {
                                        const params = new URLSearchParams();
                                        params.append('tab', "cases");
                                        if (gadgetkey == "PendingCoachingCase") {
                                            params.append('statusCases', "Pending");
                                        }
                                        if (filters?.timeFrame && filters?.rangeDate) {
                                            if (filters?.timeFrame !== 'custom') {
                                                params.append('range', filters.timeFrame);
                                            } else {
                                                const startDateParam = filters.rangeDate?.start
                                                    ? moment(filters.rangeDate.start).format('YYYY-MM-DD')
                                                    : null;
                                                const endDateParam = filters.rangeDate?.end
                                                    ? moment(filters.rangeDate.end).format('YYYY-MM-DD')
                                                    : null;

                                                if (startDateParam) {
                                                    params.append('startDate', startDateParam);
                                                }

                                                if (endDateParam) {
                                                    params.append('endDate', endDateParam);
                                                }
                                            }
                                        }

                                        if (filters?.drivers && filters?.drivers?.length > 0) {
                                            const driverIds = filters.drivers.map(driver => driver.driverId);
                                            params.append('casesDriverIds', driverIds.join(','));
                                        }

                                        if (gadgetkey == "ActiveCoachingCases" && user?.subUserId) {
                                            params.append('coachId', user?.subUserId);
                                            params.append('statusCases', ["Pending", "Monitor", "Judgement"].join(','));
                                        }

                                        const queryString = params.toString();
                                        return queryString ? `/app/driverPerformance/main?${queryString}` : '/app/driverPerformance/main';
                                    };

                                    const pendingCaseUrl = buildPendingCaseUrl();

                                    const validateSubUserId = (!user?.subUserId && gadgetkey == 'ActiveCoachingCases')

                                    return (
                                        <div className={validateSubUserId ? "pending-coaching-case-view-button-disabled" : "pending-coaching-case-view-button"}>
                                            <a href={pendingCaseUrl}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (!validateSubUserId) {
                                                        dispatch(setDriverPerformanceMainTab(3));
                                                        navigate(pendingCaseUrl);
                                                    }
                                                }}
                                            >
                                                {messages['dashboard_view']}
                                            </a>
                                        </div>
                                    )
                                    break;
                                default:
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {gadgetkey === 'RiskEventsByTag' && (
                                                <>
                                                    <IconButton
                                                        tooltip={messages['risk_events_by_tag_hierarchy_config'] || 'Tag Hierarchy'}
                                                        variant="secondary"
                                                        icon="settings"
                                                        placement="bottom"
                                                        onClick={(e) => setHierarchyAnchorEl(e.currentTarget)}
                                                    />
                                                    <Popover
                                                        open={hierarchyPopoverOpen}
                                                        anchorEl={hierarchyAnchorEl}
                                                        onClose={() => setHierarchyAnchorEl(null)}
                                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                        slotProps={{
                                                            paper: {
                                                                sx: {
                                                                    minHeight: 300,
                                                                    overflow: 'hidden',
                                                                    '& .react-select__menu-list': {
                                                                        maxHeight: 150
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ p: 2, width: 320 }}>
                                                            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                                                                {messages['risk_events_by_tag_hierarchy_config'] || 'Tag Hierarchy'}
                                                            </Typography>

                                                            <Box sx={{ mb: 2 }}>
                                                                <Select
                                                                    name="popoverHierarchy"
                                                                    textField="label"
                                                                    label={messages['risk_events_by_tag_hierarchy'] || 'Hierarchy'}
                                                                    valueField="id"
                                                                    placeholder={messages['risk_events_by_tag_hierarchy_placeholder'] || 'Select a hierarchy'}
                                                                    isMulti={false}
                                                                    options={hierarchyOptions}
                                                                    isClearable={true}
                                                                    isSearchable={true}
                                                                    value={tempHierarchy?.tagHierarchyRootId
                                                                        ? hierarchyOptions.find(opt => opt.id === tempHierarchy.tagHierarchyRootId) || null
                                                                        : null
                                                                    }
                                                                    onChange={(value) => {
                                                                        setTempHierarchy({
                                                                            tagHierarchyRootId: value?.id || null,
                                                                            tagLevel: null
                                                                        });
                                                                    }}
                                                                    defaultMenuPortalTarget
                                                                />
                                                            </Box>

                                                            {tempHierarchy?.tagHierarchyRootId && (
                                                                <Box sx={{ mb: 2 }}>
                                                                    <Select
                                                                        name="popoverLevel"
                                                                        textField="label"
                                                                        label={messages['risk_events_by_tag_level'] || 'Level'}
                                                                        valueField="id"
                                                                        placeholder={messages['risk_events_by_tag_level_placeholder'] || 'Select a level'}
                                                                        isMulti={false}
                                                                        options={hierarchyLevelOptions}
                                                                        isClearable={true}
                                                                        isSearchable={false}
                                                                        value={tempHierarchy?.tagLevel
                                                                            ? hierarchyLevelOptions.find(opt => opt.id === tempHierarchy.tagLevel) || null
                                                                            : null
                                                                        }
                                                                        onChange={(value) => {
                                                                            setTempHierarchy({
                                                                                ...tempHierarchy,
                                                                                tagLevel: value?.id || null
                                                                            });
                                                                        }}
                                                                        defaultMenuPortalTarget
                                                                    />
                                                                </Box>
                                                            )}

                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() => {
                                                                        setHierarchyAnchorEl(null);
                                                                        if (onHierarchyChange) {
                                                                            onHierarchyChange(gadgetkey, tempHierarchy);
                                                                        }
                                                                    }}
                                                                    style={{ fontSize: '0.875rem' }}
                                                                >
                                                                    {messages['apply'] || 'Apply'}
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </Popover>
                                                </>
                                            )}
                                            <TableButtons
                                                messages={messages}
                                                buttons={["export"]}
                                                onExport={(type) => {
                                                    const { drivers, tags, events, rangeDate, timeFrame, tagHierarchyRootId, tagLevel } = filters;
                                                    let conditions = [];

                                                    if (drivers.length) {
                                                        conditions.push({
                                                            field: "driverIds",
                                                            terms: drivers.map((item => item.driverId))
                                                        })
                                                    }

                                                    if (tags.length) {
                                                        conditions.push({
                                                            field: "tagIds",
                                                            terms: tags.map((item => item.id))
                                                        })
                                                    }

                                                    if (events.length) {
                                                        conditions.push({
                                                            field: "eventNames",
                                                            terms: events.map((item => item.id))
                                                        })
                                                    }

                                                    // Tag hierarchy filter conditions for export
                                                    if (tagHierarchyRootId) {
                                                        conditions.push({
                                                            field: "tagHierarchyRootId",
                                                            terms: [String(tagHierarchyRootId)]
                                                        });
                                                    }

                                                    if (tagLevel !== null && tagLevel !== undefined) {
                                                        conditions.push({
                                                            field: "tagLevel",
                                                            terms: [String(tagLevel)]
                                                        });
                                                    }
                                                    let currentSort = sort?.key ? { field: sort.key, order: sort?.order === "asc" ? "ASC" : "DESC" } : null;

                                                    const utcRange = buildUtcRange(rangeDate, timeFrame);

                                                    let payload = {
                                                        conditions: conditions,
                                                        gadgetKey: gadgetkey,
                                                        timeFrame: {
                                                            GTE: utcRange.start,
                                                            LTE: utcRange.end
                                                        },
                                                        limit: 50,
                                                        offset: 0,
                                                        nextToken: null,
                                                        sort: currentSort
                                                    }

                                                    dispatch(sendGenericExport(
                                                        `table-gadget-${gadgetkey}`,
                                                        type,
                                                        `
                                                        query (
                                                            $gadgetKey: enumDashboardGadgetKey!
                                                            $timeFrame: RangeInput
                                                            $conditions: [GadgetDataSearchInput]
                                                            $sort: GadgetDataSortInput
                                                            $limit: Int
                                                            $offset: Int
                                                            $nextToken: String
                                                        ) {
                                                            data: searchDashboardGadgetData(
                                                                gadgetKey: $gadgetKey
                                                                timeFrame: $timeFrame
                                                                limit: $limit
                                                                offset: $offset
                                                                conditions: $conditions
                                                                sort: $sort
                                                                limit: $limit
                                                                offset: $offset
                                                                nextToken: $nextToken
                                                            ) {
                                                                total
                                                                items
                                                                nextToken
                                                            }
                                                        }    
                                                    `,
                                                        payload,
                                                        getColumns(),
                                                        {
                                                            unit: 'px',
                                                            size: 'A4',
                                                            orientation: 'landscape',
                                                            fontSize: 10,
                                                        },
                                                        null,
                                                        false,
                                                        true,
                                                        generateExportStyles()
                                                    ))

                                                }}
                                            />
                                        </div>
                                    );
                            }
                        })()
                    }
                />
                <CardContent
                    style={{
                        height: calculateHeight(),
                        overflow: (gadgetkey === 'RiskEventsByDriver' || gadgetkey === 'RiskEventsByTag') ? 'hidden' : 'auto',
                        flex: 1
                    }}
                    className={cx('gadgets-table')}
                >
                    <div style={{ height: '100%' }}>
                        <TableView
                            className='table-gadget'
                            page={page}
                            ref={tableRef}
                            total={data?.total || 0}
                            keyColumn={(row, index) => `${row.driver || row.id || index}-${index}`}
                            data={displayData}
                            loading={loading}
                            topheight={paginationType === 'normal' ? 620 : null}
                            height={tableHeight()}
                            hidePagination={!enablePagination || paginationType === 'infinite'}
                            disableItemSelection
                            hideDragable
                            hideAllInPagination
                            infiniteScroll={paginationType === 'infinite'}
                            columns={columns.length ? columns
                                .filter((item) => {
                                    // riskScore: display for Driver only
                                    if (item.key === 'riskScore' && gadgetkey === 'RiskEventsByTag') return false;
                                    // avgRiskScore: display for Tag only
                                    if (item.key === 'avgRiskScore' && gadgetkey === 'RiskEventsByDriver') return false;
                                    return true;
                                })
                                .map((item) => {
                                    return {
                                        ...item,
                                        title: messages[item.title] || item.title,
                                        render: ((row) => {
                                            // avgEvents: show 'NA' when null (driver traveled < 100 miles)
                                            if (item.key === 'avgEvents' && row[item.value] === null) {
                                                return <span>NA</span>;
                                            }
                                            if (renderColumnByType) {
                                                return renderColumnByType(row, item, user, dispatch, memoizedFilters, navigate, gadgetkey, messages);
                                            }
                                            const { value } = item;
                                            const cellValue = row[value];
                                            return <span>{cellValue}</span>;
                                        }),
                                        show: true,
                                    }
                                }) : []}
                            onChangePage={(newPage) => {
                                if (paginationType === 'infinite') {
                                    loadMoreData()
                                } else {
                                    setPage(newPage);
                                    dispatch(getGadgetData({ gadgetId, gadgetkey, page: newPage, pageSize: rows, filters: filters, paginationType, sort }));
                                }
                            }}
                            {
                            ...(paginationType === 'normal'
                                ? {
                                    onChangeRowsPerPage: (newRow) => {
                                        setRows(newRow)
                                        setPage(1);
                                        dispatch(getGadgetData({ gadgetId, gadgetkey, page: 1, pageSize: newRow, filters: filters, paginationType, sort }));
                                    }
                                }
                                : {})
                            }
                            onChangeSort={(value) => {
                                setSort(value);
                                savePreferences && savePreferences([{ key: gadgetkey, value: { type: "sort", value: value } }]);

                                if (paginationType === 'normal') {
                                    setPage(1);
                                    dispatch(getGadgetData({ gadgetId, gadgetkey, page: 1, pageSize: rows, filters: filters, paginationType, sort: value }));
                                } else if (paginationType === 'infinite') {
                                    // For infinite scroll, reset search without nextToken (new search)
                                    dispatch(getGadgetData({
                                        gadgetId,
                                        gadgetkey,
                                        pageSize,
                                        filters: filters,
                                        paginationType,
                                        sort: value,
                                        nextToken: null, // New search without nextToken
                                        moreData: false // Create new list with fresh data
                                    }));
                                }
                            }}
                            nextToken={hasMoreData}
                            noMoreItemsMessage={messages['noMoreItems']}
                        />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default TableGadget;